import React from 'react';
import { Box, Typography, Paper, Divider, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Terms of Service
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Last Updated: October 3, 2025
        </Typography>
      </Box>

      <Paper sx={{ p: 4, backgroundColor: 'background.paper' }}>
        <Box sx={{ maxWidth: '800px' }}>
          
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            By accessing and using TextWiz ("the Service"), you accept and agree to be bound by these Terms of Service. 
            If you do not agree to these terms, please do not use the Service.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            2. Service Description
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            TextWiz is an AI-powered platform that transforms EPUB books into illustrated versions by generating 
            contextually relevant images using artificial intelligence. The service processes uploaded books, 
            analyzes content, and generates images that can be integrated into the book files.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            3. Copyright and Content Ownership
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            <strong>You represent and warrant that:</strong>
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                You own or have the legal right to use all books you upload to the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                You will only use the Service with legally obtained books
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                You will not upload pirated, illegally obtained, or copyright-infringing content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                You will use the Service solely for personal, non-commercial purposes
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            We reserve the right to remove any content and terminate accounts that violate copyright laws or these terms.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            4. AI-Generated Content
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            Images generated by the Service are created using artificial intelligence models based on text descriptions 
            from your uploaded books. While we strive for quality, AI-generated images may not perfectly match your 
            expectations or the original text descriptions. You retain ownership of the illustrated books you create, 
            subject to the rights of the original book content owners.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            5. Credits and Billing
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Purchase:</strong> You can purchase image generation credits through our Stripe payment system
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>Usage:</strong> Credits are consumed when images are successfully generated
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                <strong>No Refunds for Used Credits:</strong> Once credits are used to generate images, they are non-refundable
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                <strong>Fair Billing:</strong> You are only charged for successfully generated images; failed generations do not consume credits
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            6. Acceptable Use Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            <strong>You agree NOT to:</strong>
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Upload content you do not have rights to use
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Use the Service for commercial purposes without prior written permission
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Attempt to generate inappropriate, offensive, or illegal content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Share your account credentials with others
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Use automated systems or bots to access the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                Attempt to circumvent security measures or abuse the Service
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            7. Account Termination
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            We reserve the right to suspend or terminate your account at any time if we believe you have violated 
            these terms or engaged in fraudulent, abusive, or illegal activity. You may also delete your account 
            at any time through the Account settings page.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            8. Privacy and Data
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            Your use of the Service is also governed by our{' '}
            <Link href="/privacy" sx={{ color: 'primary.light', textDecoration: 'underline' }}>
              Privacy Policy
            </Link>
            , which describes how we collect, use, and protect your data.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            9. Service Modifications
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time. 
            We may also change pricing, features, or these Terms of Service with reasonable notice to users.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            10. Disclaimers and Limitation of Liability
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW:
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                We do not guarantee uninterrupted, error-free, or secure operation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                We are not liable for any indirect, incidental, or consequential damages
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                We are not responsible for third-party content, services, or actions
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            11. DMCA Copyright Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}>
            If you believe content on our Service infringes your copyright, please contact us with:
          </Typography>
          <Box component="ul" sx={{ ml: 4, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Identification of the copyrighted work
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Identification of the infringing material and its location
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
                Your contact information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                A statement of good faith belief and accuracy under penalty of perjury
              </Typography>
            </li>
          </Box>


          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
            12. Contact Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}>
            For questions about these Terms of Service, please contact us through the account page or 
            at your designated support email.
          </Typography>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              By using TextWiz, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </Typography>
          </Box>

        </Box>
      </Paper>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link
          component="button"
          onClick={() => navigate(-1)}
          sx={{ color: 'primary.light', textDecoration: 'underline', cursor: 'pointer' }}
        >
          ← Back
        </Link>
      </Box>
    </Box>
  );
};

export default TermsOfServicePage;

